import {Component, Inject, Injector, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {isAndroid, isAppleDevice} from 'helpers';
import {
  ActivationMnpInfo,
  Address, AvailableAddons, AvailableOperator, AvailableOperators,
  Contacts,
  Document,
  EntityParsed,
  EntityValueGroup, FunnelInfo,
  Identity,
  InternalPromoInfo, OfferExtras,
  PaymentMethod, SelectedAddons,
  ShippingMethod,
  SimType,
  StepId
} from 'models';
import {combineLatest, Subscription} from 'rxjs';
import {debounceTime, first} from 'rxjs/operators';
import {AnalyticsService, OfferPromoService, RecognitionService} from 'services';
import {StepsBarItem} from 'ui-kit-lib';
import {OfferAddons} from '../../../../../../app-shared/models/steps/step-offer-extras.interface';
import {SimTypeEnum} from '../../../../../../app-shared/models/steps/step-sim.interface';
import {AppStoreFacadeService} from '../../../../../facades/app-store-facade.service';
import {WINDOW} from '../../../../../providers/browser.provider';
import {ProcessStepBaseComponent} from '../../_core/process-step-base.component';

@Component({
  selector: 'sb-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent extends ProcessStepBaseComponent implements OnInit {

  public confirmEmail: string;
  public email: string;
  public mobileNumber: string;
  public customerName: string;
  public customerLastName: string;
  public taxCode: string;
  public customerDocumentType: any;
  public customerDocumentIssueDate: any;
  public customerDocumentNumber: any;
  public mnpMsisdn: string;
  public mnpOperator: string;
  public deliveryInfoRecipientZipCode: number;
  public deliveryInfoRecipientNumber: string;
  public deliveryInfoRecipientStreet: string;
  public deliveryInfoRecipientCity: string;
  public deliveryInfoRecipientAt: string;
  public deliveryInfoRecipientCountry: string;
  public deliveryInfoRecipientDistrict: string;
  public deliveryInfoRecipientFullStreet: string;
  public deliveryInfoRecipientRecipient: string;
  public residenceInfoRecipientZipCode: number;
  public residenceInfoRecipientNumber: string;
  public residenceInfoRecipientStreet: string;
  public residenceInfoRecipientCity: string;
  public residenceInfoRecipientAt: string;
  public residenceInfoRecipientCountry: string;
  public residenceInfoRecipientDistrict: string;
  public residenceInfoRecipientFullStreet: string;
  public residenceInfoRecipientRecipient: string;
  public isAndroid: boolean;
  public isAppleDevice: boolean;
  public isDesktop: boolean;
  public SIM_TYPES = SimTypeEnum;
  public simType: SimTypeEnum;
  public mgmCode: string;
  public couponCode: string;
  public mgmCodeActivationMethod = 'NEW';
  protected subscriptions: Subscription = new Subscription();
  public contacts: Contacts;
  public shipping: ShippingMethod;
  public customer: Identity;
  public payment: PaymentMethod;
  public address: Address;
  public promos: InternalPromoInfo[];
  public offerExtras: OfferExtras;
  public isSmartTopUpMandatory: boolean;
  public isSmartTopUpEnabled: boolean;
  public funnelHasCoupon: boolean;
  public isInviteCodeEnabled: boolean;
  public isCouponEnabled: boolean;
  public StepId = StepId;
  summaryForm;

  private operators: AvailableOperator[];
  public isEsim: boolean;
  private availableAddons: OfferAddons[];
  public selectedAddons: string[];
  public activeAddons: any;

  constructor(
    injector: Injector,
    public appStoreFacadeService: AppStoreFacadeService,
    public analyticsService: AnalyticsService,
    private recognitionService: RecognitionService,
    private offerPromoService: OfferPromoService,
    @Inject(WINDOW) private window: Window
  ) {
    super(injector);
  }

  ngOnInit(): void {
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepSummaryForm());
    // this.appStoreFacadeService.flow.getStepData(StepId.Summary);
    this.summaryForm = new FormGroup({
      confirm: new FormControl(true, Validators.requiredTrue)
    });
    this.manageAnalytics();
    this.isAndroid = isAndroid();
    this.isAppleDevice = isAppleDevice();
    this.isDesktop = !(this.isAndroid || this.isAppleDevice);
    this.manageESim();
    this.availableOperators();
    this.manageMGMCode();
    this.manageCoupon();
    this.getSimType();
    this.getCustomerContacts();
    this.getCustomerIdentity();
    this.getCustomerDocument();
    this.getMnpData();
    this.getDeliveryAddress();
    this.getResidenceAddress();
    this.getFunnelInfo();
    this.getOfferExtras();
  }

  public goToNextStep() {
    this.appStoreFacadeService.navigation.goNext();
  }

  private getSimType(): void {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.simType$
      ]).pipe(
        first(args => args.every(Boolean))
      ).subscribe(
        ([simTypeEntity]: [EntityParsed]) => {
          this.simType = ((simTypeEntity && simTypeEntity.value) as unknown as SimType).type;
        })
    );
  }

  getCustomerContacts() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.contacts$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([customerContacts]: [EntityValueGroup]) => {
        const {confirmEmail, email, mobileNumber } = customerContacts;
        this.confirmEmail = (customerContacts.value as unknown as Contacts).confirmEmail;
        this.email = (customerContacts.value as unknown as Contacts).email;
        this.mobileNumber = (customerContacts.value as unknown as Contacts).mobileNumber;
      })
    );
  }

  getCustomerIdentity() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.identity$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([customerIdentity]: [EntityParsed]) => {
        this.customerName = (customerIdentity.value as unknown as Identity).firstName;
        this.customerLastName = (customerIdentity.value as unknown as Identity).lastName;
        this.taxCode = (customerIdentity.value as unknown as Identity).taxCode;
      })
    );
  }

  getCustomerDocument() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.document$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([customerDocument]: [EntityParsed]) => {
        this.customerDocumentType = (customerDocument.value as unknown as Document).type;
        this.customerDocumentIssueDate = (customerDocument.value as unknown as Document).issueDate;
        this.customerDocumentNumber = (customerDocument.value as unknown as Document).number;
      })
    );
  }

  getMnpData() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.activationMnpInfo$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([activationMnpInfo]: [EntityParsed]) => {
        this.mnpMsisdn = (activationMnpInfo.value as unknown as ActivationMnpInfo).msisdn;
        this.mnpOperator = (activationMnpInfo.value as unknown as ActivationMnpInfo).operator;
        this.isEsim = (activationMnpInfo.value as unknown as ActivationMnpInfo).esim;
      })
    );
  }

  getDeliveryAddress() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.deliveryAddress$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([deliveryAddress]: [EntityParsed]) => {
        this.deliveryInfoRecipientZipCode = (deliveryAddress.value as unknown as Address).zipCode;
        this.deliveryInfoRecipientNumber = (deliveryAddress.value as unknown as Address).number;
        this.deliveryInfoRecipientStreet = (deliveryAddress.value as unknown as Address).street;
        this.deliveryInfoRecipientCity = (deliveryAddress.value as unknown as Address).city;
        this.deliveryInfoRecipientAt = (deliveryAddress.value as unknown as Address).at;
        this.deliveryInfoRecipientCountry = (deliveryAddress.value as unknown as Address).country;
        this.deliveryInfoRecipientDistrict = (deliveryAddress.value as unknown as Address).district;
        this.deliveryInfoRecipientFullStreet = (deliveryAddress.value as unknown as Address).fullStreet;
        this.deliveryInfoRecipientRecipient = (deliveryAddress.value as unknown as Address).recipient;
      })
    );
  }

  private availableOperators(): void {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.availableOperators$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([availableOperator]: [EntityValueGroup]) => {
          this.operators = (availableOperator as AvailableOperators).operators;
        })
    );
  }

  getResidenceAddress() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.residenceAddress$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([residenceAddress]: [EntityParsed]) => {
        this.residenceInfoRecipientZipCode = (residenceAddress.value as unknown as Address).zipCode;
        this.residenceInfoRecipientNumber = (residenceAddress.value as unknown as Address).number;
        this.residenceInfoRecipientStreet = (residenceAddress.value as unknown as Address).street;
        this.residenceInfoRecipientCity = (residenceAddress.value as unknown as Address).city;
        this.residenceInfoRecipientAt = (residenceAddress.value as unknown as Address).at;
        this.residenceInfoRecipientCountry = (residenceAddress.value as unknown as Address).country;
        this.residenceInfoRecipientDistrict = (residenceAddress.value as unknown as Address).district;
        this.residenceInfoRecipientFullStreet = (residenceAddress.value as unknown as Address).fullStreet;
        this.residenceInfoRecipientRecipient = (residenceAddress.value as unknown as Address).recipient;
      })
    );
  }

  getFunnelInfo() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.funnelInfo$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([funnelInfo]: [FunnelInfo]) => {
        this.isSmartTopUpMandatory = funnelInfo.isSmartTopUpMandatory;
        this.isSmartTopUpEnabled = funnelInfo.isSmartTopUpEnabled;
        this.funnelHasCoupon = funnelInfo.hasCoupon;
        this.isCouponEnabled = funnelInfo.isCouponEnabled;
        this.isInviteCodeEnabled = funnelInfo.isInviteCodeEnabled;
      })
    );
  }

  private getOfferExtras() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.offerExtras$,
        this.appStoreFacadeService.entity.selectedAddons$,
        this.appStoreFacadeService.entity.availableAddons$,
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([offerExtras, selectedAddons, availableAddons]: [EntityParsed, EntityParsed, AvailableAddons]) => {
        this.offerExtras = (offerExtras.value as unknown as OfferExtras);
        this.availableAddons = availableAddons.addons;
        this.selectedAddons = (selectedAddons.value as unknown as SelectedAddons).addons;
      })
    );
  }

  get shouldShowCouponBlock() {
    if (this.funnelHasCoupon) {
      return false;
    }
    try {
      return localStorage.getItem('enable-coupon') || this.isCouponEnabled ;
      // return window.localStorage.getItem('experimentalFeatures');
    } catch (ex) {
      return false;
    }
  }

  get shouldShowMGMBlock() {
    try {
      return localStorage.getItem('enable-mgm') || this.isInviteCodeEnabled;
      // return window.localStorage.getItem('experimentalFeatures');
    } catch (ex) {
      return false;
    }
  }

  private manageMGMCode(): void {
    this.subscriptions.add(
      combineLatest(
        [
          this.appStoreFacadeService.entity.mgmInfo$,
          this.appStoreFacadeService.entity.activationMethod$
        ]
      ).subscribe(([mgmInfo, activationMethod]) => {
        const code = mgmInfo && mgmInfo.value && mgmInfo.value.code;
        this.mgmCodeActivationMethod = (activationMethod && activationMethod.value && activationMethod.value.method || 'new').toUpperCase();
        if (!code) {
          return;
        }
        this.mgmCode = code;
      })
    );
  }

  private manageCoupon(): void {
    this.subscriptions.add(
      combineLatest(
        [ this.appStoreFacadeService.entity.couponInfo$ ]
      ).subscribe(([couponInfo]) => {
        const code = couponInfo && couponInfo.value && couponInfo.value.code;
        if (!code) {
          return;
        }
        this.couponCode = code;
      })
    );
  }

  private manageESim(): void {
    this.subscriptions.add(
      this.appStoreFacadeService.entity.simType$
        .pipe(first(Boolean))
        .subscribe(({ value }) => {
          const simType = value && value.type;
          this.simType = simType;
        })
    );
  }

  navigateTo(step) {
    this.analyticsService.sendCustomEvent({
      custom_category: 'ecommerce',
      custom_action: 'link_click',
      custom_label: `modifica ${step}`
    });
    this.appStoreFacadeService.flow.getStepData(step);
  }

  inferSimType(param: string): string {
    if (this.simType === this.SIM_TYPES.eSim) {
      return 'eSIM';
    }
    return 'SIM fisica';
    /*switch ( param ) {
      case SimTypeEnum.eSim:
        return 'eSIM';
        break;
      case SimTypeEnum.IdleSim:
        if ( this.isEsim ) {
          return 'eSIM';
        } else {
          return 'SIM fisica';
        }
        break;
      case SimTypeEnum.Standard:
        return 'SIM fisica';
        break;
    }*/
  }

  inferMnpOperators(param: string): string {
    const item1 = this.operators.find(o => o.id === param);
    return item1.name;
  }

  inferActiveAddons(param: string): string {
    const addon = this.availableAddons.find(a => a.id === param);
    return addon.title;
  }

  private manageAnalytics() {
    this.sendAnalyticsData();
  }
}
